import { auth } from '../config/firebase';

export interface TokenInfo {
  token: string;
  expiresAt: number;
  issuedAt: number;
}

export const getIdToken = async (): Promise<TokenInfo | null> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.debug('[TokenUtils] No current user found');
      return null;
    }

    console.debug('[TokenUtils] Getting ID token for user:', user.email);
    
    const token = await user.getIdToken();
    const tokenResult = await user.getIdTokenResult();
    
    const tokenInfo: TokenInfo = {
      token,
      expiresAt: new Date(tokenResult.expirationTime).getTime(),
      issuedAt: new Date(tokenResult.issuedAtTime).getTime()
    };

    console.debug('[TokenUtils] Token obtained successfully:', {
      email: user.email,
      expiresAt: new Date(tokenInfo.expiresAt).toISOString(),
      issuedAt: new Date(tokenInfo.issuedAt).toISOString(),
      tokenLength: token.length
    });


    return tokenInfo;
  } catch (error) {
    console.error('[TokenUtils] Error getting ID token:', error);
    return null;
  }
};

export const getIdTokenForced = async (): Promise<TokenInfo | null> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.debug('[TokenUtils] No current user found for forced refresh');
      return null;
    }

    console.debug('[TokenUtils] Force refreshing ID token for user:', user.email);
    
    const token = await user.getIdToken(true); // Force refresh
    const tokenResult = await user.getIdTokenResult(true);
    
    const tokenInfo: TokenInfo = {
      token,
      expiresAt: new Date(tokenResult.expirationTime).getTime(),
      issuedAt: new Date(tokenResult.issuedAtTime).getTime()
    };

    console.debug('[TokenUtils] Token force refreshed successfully:', {
      email: user.email,
      expiresAt: new Date(tokenInfo.expiresAt).toISOString(),
      issuedAt: new Date(tokenInfo.issuedAt).toISOString(),
      tokenLength: token.length
    });


    return tokenInfo;
  } catch (error) {
    console.error('[TokenUtils] Error force refreshing ID token:', error);
    return null;
  }
};

export const isTokenExpired = (tokenInfo: TokenInfo | null): boolean => {
  if (!tokenInfo) return true;
  
  const now = Date.now();
  const bufferTime = 5 * 60 * 1000; // 5 minutes buffer
  const isExpired = now >= (tokenInfo.expiresAt - bufferTime);
  
  if (isExpired) {
    console.debug('[TokenUtils] Token is expired or expires soon:', {
      now: new Date(now).toISOString(),
      expiresAt: new Date(tokenInfo.expiresAt).toISOString(),
      timeUntilExpiry: Math.floor((tokenInfo.expiresAt - now) / 1000 / 60)
    });
  }
  
  return isExpired;
};

export const getValidToken = async (): Promise<string | null> => {
  try {
    console.debug('[TokenUtils] Getting valid token...');
    
    const tokenInfo = await getIdToken();
    
    if (!tokenInfo) {
      console.debug('[TokenUtils] No token available');
      return null;
    }
    
    if (isTokenExpired(tokenInfo)) {
      console.debug('[TokenUtils] Token expired, refreshing...');
      const refreshedTokenInfo = await getIdTokenForced();
      return refreshedTokenInfo?.token || null;
    }
    
    return tokenInfo.token;
  } catch (error) {
    console.error('[TokenUtils] Error getting valid token:', error);
    return null;
  }
};

export const clearStoredToken = (): void => {
  try {
    localStorage.removeItem('auth_token');
    sessionStorage.removeItem('auth_token');
    console.debug('[TokenUtils] Stored tokens cleared');
  } catch (error) {
    console.error('[TokenUtils] Error clearing stored tokens:', error);
  }
};

export const validateAndRefreshToken = async (): Promise<{ isValid: boolean; token: string | null }> => {
  try {
    console.debug('[TokenUtils] Validating and refreshing token if needed...');
    
    // First check if user is authenticated with Firebase
    const user = auth.currentUser;
    if (!user) {
      console.debug('[TokenUtils] No Firebase user, token invalid');
      return { isValid: false, token: null };
    }
    
    // Get current token info
    let tokenInfo = await getIdToken();
    
    if (!tokenInfo) {
      console.debug('[TokenUtils] No token available, invalid');
      return { isValid: false, token: null };
    }
    
    // Check if token is expired or expiring soon
    if (isTokenExpired(tokenInfo)) {
      console.debug('[TokenUtils] Token expired or expiring soon, refreshing...');
      tokenInfo = await getIdTokenForced();
      
      if (!tokenInfo) {
        console.debug('[TokenUtils] Failed to refresh token');
        return { isValid: false, token: null };
      }
      
      // Store the refreshed token
      storeToken(tokenInfo);
      console.debug('[TokenUtils] Token refreshed and stored successfully');
    }
    
    return { isValid: true, token: tokenInfo.token };
  } catch (error) {
    console.error('[TokenUtils] Error validating/refreshing token:', error);
    return { isValid: false, token: null };
  }
};

export const storeToken = (tokenInfo: TokenInfo): void => {
  try {
    const tokenData = JSON.stringify(tokenInfo);
    localStorage.setItem('auth_token', tokenData);
    console.debug('[TokenUtils] Token stored in localStorage:', {
      expiresAt: new Date(tokenInfo.expiresAt).toISOString(),
      tokenLength: tokenInfo.token.length
    });
  } catch (error) {
    console.error('[TokenUtils] Error storing token:', error);
  }
};

export const getStoredToken = (): TokenInfo | null => {
  try {
    const tokenData = localStorage.getItem('auth_token');
    if (!tokenData) {
      console.debug('[TokenUtils] No stored token found');
      return null;
    }
    
    const tokenInfo: TokenInfo = JSON.parse(tokenData);
    
    if (isTokenExpired(tokenInfo)) {
      console.debug('[TokenUtils] Stored token is expired, removing...');
      clearStoredToken();
      return null;
    }
    
    console.debug('[TokenUtils] Valid stored token found:', {
      expiresAt: new Date(tokenInfo.expiresAt).toISOString(),
      tokenLength: tokenInfo.token.length
    });
    
    return tokenInfo;
  } catch (error) {
    console.error('[TokenUtils] Error getting stored token:', error);
    clearStoredToken(); // Clear corrupted data
    return null;
  }
};